import React, { useState, useEffect } from 'react';
import { Server_URL, orgScenarios_URL, scenario_URL, scenario_openX_URL, deleteOrgScenario_URL } from '../../constants/constants';
import CustomIconModal from '../Modal';
import { FaDownload, FaEye } from 'react-icons/fa';
import { BsFiletypeJson } from "react-icons/bs";
import { MdOutlineScreenshotMonitor } from "react-icons/md";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { Calendar } from 'primereact/calendar';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import LoadingSpinner from '../LoadingSpinner';
import DeleteButtonById from '../admin/DeleteButton';

function OrganisationScenarios({ organisationId }) {
    const [organisationData, setOrganisationData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [loadingScreenshot, setLoadingScreenshot] = useState({});
    const [loadingOpenX, setLoadingOpenX] = useState({});
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters] = useState({
        'global': { value: null, matchMode: FilterMatchMode.CONTAINS },
        'date': { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
    });


    useEffect(() => {
        if (!organisationId) return;

        const fetchScenarios = async () => {
            try {
                const token = document.cookie.replace(
                    /(?:(?:^|.*;\s*)token\s*=\s*([^;]*).*$)|^.*$/,
                    '$1'
                );
                const response = await fetch(`${Server_URL}${orgScenarios_URL}`, {
                    method: 'GET',
                    credentials: 'include',
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (!response.ok) throw new Error('Failed to fetch scenarios');

                const data = await response.json();
                console.log('Raw data from API:', data);          // ← add this
                console.log('Data length:', data.length);

                const mapped = data.map(item => ({
                    ...item,
                    date: new Date(item.date),
                }));

                setOrganisationData(mapped);
            } catch (err) {
                console.error('Error fetching scenarios:', err);
                setError('Failed to load scenarios. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchScenarios();
    }, [organisationId]);

    const onDeleteSuccess = (id) => {
        setOrganisationData(prevData => prevData.filter(item => item.scenarioId !== id));
    };
    const downloadScenarioScreenshot = async (rowData) => {
        setLoadingScreenshot(prev => ({ ...prev, [rowData.scenarioId]: true }));
        try {
            const token = document.cookie.replace(/(?:(?:^|.*;\s*)token\s*=\s*([^;]*).*$)|^.*$/, '$1');
            const response = await fetch(`${Server_URL}${scenario_URL}/${rowData.screenshotFileGuid}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (!response.ok) {
                setError('Failed to download file. Please contact support.');
                throw new Error('Failed to download screenshot');
            }

            setError('');
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = rowData.screenshotFileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error('Error downloading screenshot:', err);
        }
        setLoadingScreenshot(prev => ({ ...prev, [rowData.scenarioId]: false }));
    };

    const downloadScenarioOpenX = async (rowData) => {
        setLoadingOpenX(prev => ({ ...prev, [rowData.scenarioId]: true }));
        try {
            console.log("Attempting to download OpenX file");
            const token = document.cookie.replace(/(?:(?:^|.*;\s*)token\s*=\s*([^;]*).*$)|^.*$/, '$1');
            const response = await fetch(`${Server_URL}${scenario_openX_URL}?openScenario=${encodeURIComponent(rowData.openXFileGuid)}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (!response.ok) {
                setError('Failed to download file. Please contact support.');
                throw new Error('Failed to download OpenX file');
            }

            setError('');
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = rowData.openXFileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error('Error downloading OpenX file:', err);
        }
        setLoadingOpenX(prev => ({ ...prev, [rowData.scenarioId]: false }));
    };

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters['global'].value = value;
        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const formatDate = (value) => {
        return value.toLocaleDateString('en-UK', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    };

    // ── Column Templates ──────────────────────────────────────────────────────

    const dateBodyTemplate = (rowData) => formatDate(rowData.date);

    const dateFilterTemplate = (options) => (
        <Calendar
            value={options.value}
            onChange={(e) => options.filterCallback(e.value, options.index)}
            dateFormat="mm/dd/yy"
            placeholder="mm/dd/yyyy"
            mask="99/99/9999"
        />
    );

    const scenarioNameBodyTemplate = (rowData) => (
        <React.Fragment>
            <span>{rowData.scenarioName}</span>
        </React.Fragment>
    );
    const organisationNameBodyTemplate = (rowData) => (
        <React.Fragment>
            <span>{rowData.orgName}</span>
        </React.Fragment>
    );

    const versionBodyTemplate = (rowData) => (
        <React.Fragment>
            <span>{rowData.version}</span>
        </React.Fragment>
    );

    const ScenarioViewBodyTemplate = (rowData) => (
        <>
            {/* {rowData.screenshotFileGuid ? (
                <Button
                    onClick={() => downloadScenarioScreenshot(rowData)}
                    disabled={!!loadingScreenshot[rowData.scenarioId]}
                    className="btn orange-color"
                    title="Download Screenshot"
                >
                    {loadingScreenshot[rowData.scenarioId] ? (
                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                    ) : (
                        <><MdOutlineScreenshotMonitor /> <FaDownload /></>
                    )}
                </Button>
            ) : (
                <span>No Screenshot</span>
            )} */}

            <Button
                onClick={() => downloadScenarioOpenX(rowData)}
                disabled={!!loadingOpenX[rowData.scenarioId]}
                className="btn orange-color"
                style={{ marginLeft: '10px' }}
                title="Download OpenX"
            >
                {loadingOpenX[rowData.scenarioId] ? (
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                ) : (
                    <>OpenX <FaDownload /></>
                )}
            </Button>

            <DeleteButtonById type='Scenario' id={rowData.scenarioId} url={deleteOrgScenario_URL} onDeleteSuccess={onDeleteSuccess} />
        </>
    );
    const renderHeader = () => (
        <>
            {error && <div className="alert alert-danger">{error}</div>}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ display: 'flex', alignItems: 'center' }}>
                    <i className="pi pi-search" />
                    <InputText
                        value={globalFilterValue}
                        onChange={onGlobalFilterChange}
                        placeholder="Keyword search..."
                    />
                </span>
            </div>
        </>
    );

    const header = renderHeader();

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <>
            {loading ? (
                <LoadingSpinner />
            ) : error ? (
                <div className="alert alert-danger">{error}</div>
            ) : (
                <DataTable
                    header={header}
                    value={organisationData}
                    globalFilterFields={['scenarioName', 'date']}
                    emptyMessage="No scenarios found for your organisation."
                    filters={filters}
                    stripedRows
                    tableStyle={{ minWidth: '50rem' }}
                    paginator
                    rows={10}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                >
                    <Column
                        field="orgName"
                        header="Organisation"
                        style={{ minWidth: '5rem' }}
                        sortable
                        body={organisationNameBodyTemplate}
                    />
                    <Column
                        field="scenarioName"
                        header="Scenario Name"
                        style={{ minWidth: '5rem' }}
                        sortable
                        filter
                        filterMatchMode="contains"
                        filterPlaceholder="Keyword search..."
                        body={scenarioNameBodyTemplate}
                    />
                    <Column
                        field="date"
                        header="Date"
                        style={{ minWidth: '5rem' }}
                        sortable
                        filterField="date"
                        dataType="date"
                        body={dateBodyTemplate}
                        filter
                        filterElement={dateFilterTemplate}
                        sortField="date"
                        sortOrder={-1}
                    />
                    <Column
                        field="version"
                        header="Version"
                        style={{ minWidth: '5rem' }}
                        sortable
                        filter
                        filterMatchMode="contains"
                        filterPlaceholder="Keyword search..."
                        body={versionBodyTemplate}
                    />
                    <Column
                        field="scenarioDefintion"
                        header="Submitted Scenarios"
                        style={{ minWidth: '25rem' }}
                        body={ScenarioViewBodyTemplate}
                    />
                </DataTable>
            )}
        </>
    );
}

export default OrganisationScenarios;